import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import { styled } from "@material-ui/core";
import { InvoiceStatus } from "elephant-web/gql";
import { usePrintableInvoiceCreatedByQuery } from "../__generated__/use-fetch-printable-invoice-rxdb.gql";
import type { V2InvoiceCardProps } from "../tab-content-types";
import { useFetchInvoiceRxdb } from "../use-fetch-invoice-rxdb";
import { isZeroMoney } from "./_lib/invoice-money";
import { InvoiceStatusChip } from "./_lib/invoice-status-chip";
import { formatInvoiceDate } from "./_lib/use-invoice-card-data";
import { useInvoiceDeeplink } from "./_lib/use-invoice-deeplink";
import { useInvoiceMoneyFormat } from "./_lib/use-invoice-money-format";
import { DraftInvoiceCard } from "./draft-invoice-card";
import { FinalisedInvoiceCard } from "./finalised-invoice-card";
import { InvoiceCardHeader } from "./invoice-card-header";
import { InvoiceCardShell } from "./invoice-card-shell";

const CARD_SPACING = 16;

/** The collapse control is the same button either way; only its meaning flips. */
const CollapsedChrome = styled("div")({
  "& .MuiSvgIcon-root": { transform: "rotate(180deg)" },
});

export const patientDueInPence = (
  summary: V2InvoiceCardProps["invoiceSummary"],
) => {
  const patient = summary.payers?.find((payer) => payer.type === "PATIENT");
  return patient?.balance.due ?? 0;
};

/**
 * Deliberately does not open a document subscription — a patient's invoice
 * history can run to dozens of rows.
 */
const CollapsedInvoiceCard: FC<
  Pick<V2InvoiceCardProps, "invoiceSummary"> & {
    dateLabel: string;
    onToggleExpanded: () => void;
  }
> = ({ invoiceSummary, dateLabel, onToggleExpanded }) => {
  const convertFromPence = useConvertFromPenceFactory();
  const formatMoney = useInvoiceMoneyFormat(invoiceSummary.balance.currency);
  const due = Number(convertFromPence(patientDueInPence(invoiceSummary)));
  const total = Number(convertFromPence(invoiceSummary.balance.total ?? 0));

  return (
    <CollapsedChrome>
      <InvoiceCardHeader
        dateLabel={dateLabel}
        encounterTypeLabel={invoiceSummary.patientType}
        amount={formatMoney(due)}
        note={
          isZeroMoney(due)
            ? `paid in full · ${formatMoney(total)} billed`
            : `due from patient · ${formatMoney(total)} billed`
        }
        chip={<InvoiceStatusChip status={invoiceSummary.invoiceStatus} />}
        onCollapse={onToggleExpanded}
        collapseLabel="Expand invoice"
      />
    </CollapsedChrome>
  );
};

const ExpandedInvoiceCard: FC<
  V2InvoiceCardProps & { dateLabel: string; onToggleExpanded: () => void }
> = ({ invoiceId, invoiceSummary, priceList, dateLabel, onToggleExpanded }) => {
  const { doc } = useFetchInvoiceRxdb(invoiceId);

  // api-user data has no RxDB collection, so the clinician's name is the one
  // thing on the card that needs a network round trip. `errorPolicy: "ignore"`
  // keeps an offline card rendering; the header then omits the clinician
  // rather than trailing a separator.
  const createdByUserId = doc?.meta?.createdByUserId ?? "";
  const { data } = usePrintableInvoiceCreatedByQuery({
    variables: { userId: createdByUserId },
    skip: !createdByUserId,
    fetchPolicy: "cache-first",
    errorPolicy: "ignore",
  });
  const clinicianName =
    [data?.user?.person?.firstname, data?.user?.person?.lastname]
      .filter(Boolean)
      .join(" ") || undefined;

  if (!doc) {
    return (
      <CollapsedInvoiceCard
        invoiceSummary={invoiceSummary}
        dateLabel={dateLabel}
        onToggleExpanded={onToggleExpanded}
      />
    );
  }

  const shared = {
    doc,
    patientId: doc.patientId,
    priceList,
    dateLabel,
    clinicianName,
    onToggleExpanded,
  };

  return doc.invoiceStatus === InvoiceStatus.Draft ? (
    <DraftInvoiceCard {...shared} />
  ) : (
    <FinalisedInvoiceCard {...shared} />
  );
};

export const InvoiceCardContainer: FC<
  V2InvoiceCardProps & { defaultExpanded: boolean }
> = ({ defaultExpanded, ...invoice }) => {
  const { expanded, toggleExpanded } = useInvoiceDeeplink({
    invoiceId: invoice.invoiceId,
    defaultExpanded,
  });
  const dateLabel = formatInvoiceDate(new Date(invoice.invoiceSummary.created));

  return (
    <InvoiceCardShell
      style={{ marginBottom: CARD_SPACING }}
      data-testid={`invoicing.v2.card.${invoice.invoiceId}`}
    >
      {expanded ? (
        <ExpandedInvoiceCard
          {...invoice}
          dateLabel={dateLabel}
          onToggleExpanded={toggleExpanded}
        />
      ) : (
        <CollapsedInvoiceCard
          invoiceSummary={invoice.invoiceSummary}
          dateLabel={dateLabel}
          onToggleExpanded={toggleExpanded}
        />
      )}
    </InvoiceCardShell>
  );
};
